import { nextRouteStop, type MbmtRouteStop } from './useMbmtApi'
import type { BusBoardRow, BusRouteStop, BusRouteSummary } from './types'

/**
 * TS mirror of tools/nagpur_route_resolve.py -- keep the two in lockstep; that module's
 * own docstring is the full writeup of WHY this exists (EDMS's schedule_code carries no
 * Up/Down signal at all). STATUS_RESOLVED means route_number + depot alone narrow to
 * exactly one route_id -- today, only LOOP routes get there this way (confirmed live
 * 2026-11-04: 20/250 board rows), since Up/Down-split routes always leave 2 candidates
 * on that alone.
 *
 * STATUS_RESOLVED_GEOMETRIC is the second, additive path: the server's own trail-based
 * direction inference (tools/nagpur_direction_resolve.py) cleanly discriminated an
 * otherwise-STATUS_AMBIGUOUS_DIRECTION duty's two candidates, using the bus's real GPS
 * trail history, not a single instantaneous heading reading -- see that module's own
 * docstring for the live-tested confidence gates this requires and why a naive
 * single-heading approach was confirmed too failure-prone on 2026-11-04 (see this
 * file's own git history for that finding) before being fixed with trail history
 * instead of abandoned. The console cannot compute this itself (it needs GPS-trail
 * history the live board does not otherwise carry) -- it only ever consumes the
 * server's own result via BusBoardRow.geometry_resolved_route_id/_direction, passed
 * into resolveVehicle's optional geometryHint parameter. Kept as a DISTINCT status from
 * STATUS_RESOLVED on purpose: an inferred direction is not the same certainty as a
 * structural one, and conflating them would undo the whole point of this file's own
 * honest-status-label convention for every other ambiguous case.
 */
export const STATUS_RESOLVED = 'resolved'
export const STATUS_RESOLVED_GEOMETRIC = 'resolved_geometric'
export const STATUS_UNMAPPED = 'unmapped'
export const STATUS_DEPOT_MISMATCH = 'depot_mismatch'
export const STATUS_AMBIGUOUS_DIRECTION = 'ambiguous_direction'
export const STATUS_AMBIGUOUS_DEPOT_AND_DIRECTION = 'ambiguous_depot_and_direction'
export const STATUS_UNPARSED = 'unparsed_schedule_code'

export interface NagpurRouteResolution {
  status: string
  routeId: string | null
  routeNumber: string | null
  candidates: string[]
}

// Mirrors tools/nagpur_route_resolve.py's _SCHEDULE_CODE_RE exactly -- see that
// module's own comment for why the seq group allows one trailing letter ("04A").
const SCHEDULE_CODE_RE = /^([0-9]+[A-Za-z]*)[A-Z]\/(\d+[A-Za-z]?)\.$/

export function parseRouteNumber(scheduleCode: string | null | undefined): string | null {
  if (!scheduleCode) return null
  const m = SCHEDULE_CODE_RE.exec(scheduleCode.trim())
  return m ? m[1]! : null
}

export function resolveRouteId(
  routeNumber: string,
  homeDepotId: string | null | undefined,
  routes: BusRouteSummary[],
): { status: string; routeId: string | null; candidates: string[] } {
  const allCandidates = routes.filter((r) => r.route_number === routeNumber)
  if (allCandidates.length === 0) {
    return { status: STATUS_UNMAPPED, routeId: null, candidates: [] }
  }

  let pool = allCandidates
  if (homeDepotId) {
    const depotCandidates = allCandidates.filter((r) => r.depot_id === homeDepotId)
    if (depotCandidates.length === 0) {
      return {
        status: STATUS_DEPOT_MISMATCH,
        routeId: null,
        candidates: allCandidates.map((r) => r.route_id),
      }
    }
    pool = depotCandidates
  }

  if (pool.length === 1) {
    return { status: STATUS_RESOLVED, routeId: pool[0]!.route_id, candidates: [pool[0]!.route_id] }
  }
  const status = homeDepotId ? STATUS_AMBIGUOUS_DIRECTION : STATUS_AMBIGUOUS_DEPOT_AND_DIRECTION
  return { status, routeId: null, candidates: pool.map((r) => r.route_id) }
}

/** geometryHint carries the server's own BusBoardRow.geometry_resolved_route_id/
 *  _direction -- only ever applied when the structural pass alone comes back
 *  STATUS_AMBIGUOUS_DIRECTION AND the hinted route_id is one of that ambiguity's own
 *  candidates (never trusted blindly: a stale/mismatched hint must not override a
 *  structural result that disagrees with it). */
export function resolveVehicle(
  scheduleCode: string | null | undefined,
  homeDepotId: string | null | undefined,
  routes: BusRouteSummary[],
  geometryHint?: { routeId: string | null; direction: string | null } | null,
): NagpurRouteResolution {
  const routeNumber = parseRouteNumber(scheduleCode)
  if (routeNumber === null) {
    return { status: STATUS_UNPARSED, routeId: null, routeNumber: null, candidates: [] }
  }
  const structural = resolveRouteId(routeNumber, homeDepotId, routes)
  if (
    structural.status === STATUS_AMBIGUOUS_DIRECTION &&
    geometryHint?.routeId &&
    structural.candidates.includes(geometryHint.routeId)
  ) {
    return {
      status: STATUS_RESOLVED_GEOMETRIC,
      routeId: geometryHint.routeId,
      routeNumber,
      candidates: structural.candidates,
    }
  }
  return { ...structural, routeNumber }
}

/** Direct instruction: route/stops/next-stop/ETA only show once the bus has actually
 *  outshed (left the depot) for its mapped duty -- a resolved duty alone is not
 *  sufficient. A bus still sitting in_depot has nothing live to show yet (no "next
 *  stop" exists until it is actually moving along the route), and highlighting the
 *  full route for a parked bus would read as "this bus is out on this route" when it
 *  is not. no_signal is excluded too -- a bus that stopped transmitting is not
 *  confirmed to still be running its route either, same reasoning
 *  _nagpur_presence's own "no_signal, not out_on_route forever" fix applies live-side.
 *  Single source of truth, shared by NagpurBusDetailPanel.tsx (the side panel) and
 *  BusRouteMap.tsx (the map highlight) so the two can never disagree about when a
 *  resolved duty actually becomes visible. */
export function hasOutshed(status: BusBoardRow['status']): boolean {
  return status === 'out_on_route'
}

/** The one routeId either caller should ever draw/display for a vehicle -- resolution
 *  AND outshed, both required. Centralizing this (not just hasOutshed) means the two
 *  call sites can't independently drift on HOW the two checks combine. */
export function visibleResolvedRouteId(
  vehicle:
    | Pick<
        BusBoardRow,
        | 'schedule_code'
        | 'home_depot_id'
        | 'status'
        | 'geometry_resolved_route_id'
        | 'geometry_resolved_direction'
      >
    | null
    | undefined,
  routes: BusRouteSummary[],
): string | null {
  if (!vehicle || !hasOutshed(vehicle.status)) return null
  const r = resolveVehicle(vehicle.schedule_code, vehicle.home_depot_id, routes, {
    routeId: vehicle.geometry_resolved_route_id,
    direction: vehicle.geometry_resolved_direction,
  })
  return r.status === STATUS_RESOLVED || r.status === STATUS_RESOLVED_GEOMETRIC ? r.routeId : null
}

/** Adapts a resolved route's own (already direction-unambiguous) stop list into
 *  nextRouteStop()'s MbmtRouteStop shape -- same nearest/ahead-behind geometry MBMT's
 *  map already uses, reused rather than reimplemented. The constant `direction` value
 *  is a trivial single-leg stand-in: nextRouteStop's own leg-scoping filter
 *  (`usable.filter(s => s.direction === nearest.direction)`) only matters when two
 *  different legs are merged into one list, which never happens here -- a resolved
 *  route_id already IS one leg. */
function toMbmtStop(s: BusRouteStop): MbmtRouteStop {
  return {
    direction: 'resolved', seq: s.sequence_no, source: 'bus_route_stop',
    stage_id: null, name: s.stop_name, lat: s.lat, lon: s.lon, along: null,
  }
}

export function nextBusRouteStop(
  stops: BusRouteStop[], pos: { lat: number; lon: number }, opts?: { moving?: boolean },
): BusRouteStop | null {
  const next = nextRouteStop(stops.map(toMbmtStop), pos, opts)
  if (!next) return null
  return stops.find((s) => s.sequence_no === next.seq) ?? null
}
